import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Plus, Trash2, Eye, EyeOff, Copy, Check, ToggleLeft, ToggleRight, Pencil, X, Link, Unlink, Loader } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function Settings() {
  const { isAdmin, user: currentUser } = useAuth();
  const [tab, setTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [newToken, setNewToken] = useState(null); // { token, name }
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState('#6366F1');
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [editStatusForm, setEditStatusForm] = useState({ name: '', color: '' });
  const [serviceCallTypes, setServiceCallTypes] = useState([]);
  const [newTypeName, setNewTypeName] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'user' });
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ name: '', role: 'user', password: '' });
  const [newFavName, setNewFavName] = useState('');
  const [newFavPrice, setNewFavPrice] = useState('');
  const [editingFavIndex, setEditingFavIndex] = useState(null);
  const [editFavForm, setEditFavForm] = useState({ name: '', price: '' });
  const [googleStatus, setGoogleStatus] = useState(null); // null = loading, { connected, email?, connected_at? }
  const [googleConnecting, setGoogleConnecting] = useState(false);

  // Calls tab
  const [callLabels, setCallLabels] = useState([]);
  const [newCallLabel, setNewCallLabel] = useState({ label: '', value: '', sort_order: '' });
  const [editingCallLabel, setEditingCallLabel] = useState(null); // { id, label, value, sort_order }
  const [interestTypes, setInterestTypes] = useState([]);
  const [newInterestName, setNewInterestName] = useState('');
  const [editingInterestType, setEditingInterestType] = useState(null); // { id, name }

  useEffect(() => {
    Promise.all([
      api.get('/settings'),
      api.get('/statuses'),
      api.get('/service-call-types'),
      isAdmin ? api.get('/settings/tokens') : Promise.resolve({ data: [] }),
      isAdmin ? api.get('/settings/users') : Promise.resolve({ data: [] }),
      isAdmin ? api.get('/settings/google/status') : Promise.resolve({ data: null }),
      api.get('/settings/call-labels'),
      api.get('/settings/interest-types'),
    ]).then(([settingsRes, statusesRes, typesRes, tokensRes, usersRes, googleRes, callLabelsRes, interestTypesRes]) => {
      setSettings(settingsRes.data);
      setStatuses(statusesRes.data);
      setServiceCallTypes(typesRes.data);
      setTokens(tokensRes.data);
      setUsers(usersRes.data);
      setGoogleStatus(googleRes.data);
      setCallLabels(callLabelsRes.data || []);
      setInterestTypes(interestTypesRes.data || []);
    }).catch(console.error)
      .finally(() => setLoading(false));

    // Handle redirect back from Google OAuth
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected')) {
      setTab('integrations');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      window.history.replaceState({}, '', window.location.pathname);
      if (isAdmin) {
        api.get('/settings/google/status').then((r) => setGoogleStatus(r.data)).catch(() => {});
      }
    } else if (params.get('google_error')) {
      alert('שגיאה בחיבור חשבון Google: ' + params.get('google_error'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [isAdmin]);

  async function addServiceCallType() {
    if (!newTypeName.trim()) return;
    try {
      const res = await api.post('/service-call-types', { name: newTypeName.trim() });
      setServiceCallTypes((t) => [...t, res.data]);
      setNewTypeName('');
    } catch (err) { alert(err.response?.data?.error || 'שגיאה'); }
  }

  async function deleteServiceCallType(id) {
    if (!window.confirm('למחוק סוג קריאה זה?')) return;
    try {
      await api.delete(`/service-call-types/${id}`);
      setServiceCallTypes((t) => t.filter((x) => x.id !== id));
    } catch (err) { alert(err.response?.data?.error || 'שגיאה — ייתכן שהסוג בשימוש'); }
  }

  async function saveSetting(key, value) {
    try {
      await api.put('/settings', { [key]: value });
      setSettings((s) => ({ ...s, [key]: value }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { alert('שגיאה בשמירה'); }
  }

  function getFavItems() {
    return Array.isArray(settings.favorite_items) ? settings.favorite_items : [];
  }

  async function addFavoriteItem() {
    if (!newFavName.trim()) return;
    const updated = [...getFavItems(), { name: newFavName.trim(), price: newFavPrice ? parseFloat(newFavPrice) : null }];
    await saveSetting('favorite_items', updated);
    setNewFavName('');
    setNewFavPrice('');
  }

  async function removeFavoriteItem(index) {
    const updated = getFavItems().filter((_, i) => i !== index);
    await saveSetting('favorite_items', updated);
  }

  async function saveFavoriteItem(index) {
    if (!editFavForm.name.trim()) return;
    const updated = getFavItems().map((item, i) =>
      i === index
        ? { name: editFavForm.name.trim(), price: editFavForm.price !== '' ? parseFloat(editFavForm.price) : null }
        : item
    );
    await saveSetting('favorite_items', updated);
    setEditingFavIndex(null);
  }

  async function addStatus() {
    if (!newStatusName.trim()) return;
    try {
      const res = await api.post('/statuses', { name: newStatusName, color: newStatusColor });
      setStatuses((s) => [...s, res.data]);
      setNewStatusName('');
    } catch (err) { alert(err.response?.data?.error || 'שגיאה'); }
  }

  async function deleteStatus(id) {
    if (!window.confirm('למחוק סטטוס זה?')) return;
    try {
      await api.delete(`/statuses/${id}`);
      setStatuses((s) => s.filter((st) => st.id !== id));
    } catch (err) { alert(err.response?.data?.error || 'שגיאה - ייתכן שהסטטוס בשימוש'); }
  }

  function startEditStatus(s) {
    setEditingStatusId(s.id);
    setEditStatusForm({ name: s.name, color: s.color });
  }

  async function saveEditStatus() {
    try {
      const res = await api.put(`/statuses/${editingStatusId}`, editStatusForm);
      setStatuses((s) => s.map((x) => x.id === editingStatusId ? { ...x, ...res.data } : x));
      setEditingStatusId(null);
    } catch { alert('שגיאה בעדכון סטטוס'); }
  }

  async function createToken() {
    const name = window.prompt('שם לטוקן API:');
    if (!name) return;
    try {
      const res = await api.post('/settings/tokens', { name });
      setNewToken(res.data);
      setTokens((t) => [...t, res.data]);
    } catch { alert('שגיאה ביצירת טוקן'); }
  }

  async function addUser() {
    if (!newUserForm.name.trim() || !newUserForm.email.trim() || !newUserForm.password.trim()) {
      alert('שם, אימייל וסיסמה הם שדות חובה');
      return;
    }
    try {
      const res = await api.post('/settings/users', newUserForm);
      setUsers((u) => [...u, res.data]);
      setShowAddUser(false);
      setNewUserForm({ name: '', email: '', password: '', role: 'user' });
    } catch (err) { alert(err.response?.data?.error || 'שגיאה ביצירת משתמש'); }
  }

  function startEditUser(u) {
    setEditingUser(u);
    setEditUserForm({ name: u.name, role: u.role, password: '' });
  }

  async function saveEditUser() {
    const updates = { name: editUserForm.name.trim(), role: editUserForm.role };
    if (editUserForm.password.trim()) updates.password = editUserForm.password;
    try {
      const res = await api.put(`/settings/users/${editingUser.id}`, updates);
      setUsers((u) => u.map((x) => x.id === editingUser.id ? { ...x, ...res.data } : x));
      setEditingUser(null);
    } catch { alert('שגיאה בעדכון משתמש'); }
  }

  async function toggleActive(userId, current) {
    try {
      const res = await api.put(`/settings/users/${userId}`, { is_active: !current });
      setUsers((u) => u.map((x) => x.id === userId ? { ...x, is_active: res.data.is_active } : x));
    } catch { alert('שגיאה'); }
  }

  async function revokeToken(id) {
    if (!window.confirm('לבטל טוקן זה?')) return;
    try {
      await api.delete(`/settings/tokens/${id}`);
      setTokens((t) => t.filter((tk) => tk.id !== id));
    } catch { alert('שגיאה'); }
  }

  // ── Call Labels ──────────────────────────────────────────────────
  async function addCallLabel() {
    if (!newCallLabel.label.trim() || !newCallLabel.value.trim()) return;
    try {
      const res = await api.post('/settings/call-labels', {
        label: newCallLabel.label.trim(),
        value: newCallLabel.value.trim(),
        sort_order: newCallLabel.sort_order ? parseInt(newCallLabel.sort_order) : 99,
      });
      setCallLabels((l) => [...l, res.data]);
      setNewCallLabel({ label: '', value: '', sort_order: '' });
    } catch (err) { alert(err.response?.data?.error || 'שגיאה'); }
  }

  async function saveCallLabel() {
    if (!editingCallLabel) return;
    try {
      const res = await api.put(`/settings/call-labels/${editingCallLabel.id}`, {
        label: editingCallLabel.label,
        value: editingCallLabel.value,
        sort_order: editingCallLabel.sort_order ? parseInt(editingCallLabel.sort_order) : 99,
      });
      setCallLabels((l) => l.map((x) => x.id === editingCallLabel.id ? res.data : x));
      setEditingCallLabel(null);
    } catch { alert('שגיאה'); }
  }

  async function deleteCallLabel(id) {
    if (!window.confirm('למחוק תגית זו?')) return;
    try {
      await api.delete(`/settings/call-labels/${id}`);
      setCallLabels((l) => l.filter((x) => x.id !== id));
    } catch { alert('שגיאה'); }
  }

  // ── Interest Types ────────────────────────────────────────────────
  async function addInterestType() {
    if (!newInterestName.trim()) return;
    try {
      const res = await api.post('/settings/interest-types', { name: newInterestName.trim() });
      setInterestTypes((t) => [...t, res.data]);
      setNewInterestName('');
    } catch (err) { alert(err.response?.data?.error || 'שגיאה'); }
  }

  async function saveInterestType() {
    if (!editingInterestType) return;
    try {
      const res = await api.put(`/settings/interest-types/${editingInterestType.id}`, { name: editingInterestType.name });
      setInterestTypes((t) => t.map((x) => x.id === editingInterestType.id ? res.data : x));
      setEditingInterestType(null);
    } catch { alert('שגיאה'); }
  }

  async function deleteInterestType(id) {
    if (!window.confirm('למחוק תחום עניין זה?')) return;
    try {
      await api.delete(`/settings/interest-types/${id}`);
      setInterestTypes((t) => t.filter((x) => x.id !== id));
    } catch { alert('שגיאה'); }
  }

  async function connectGoogle() {
    setGoogleConnecting(true);
    try {
      const res = await api.get('/settings/google/auth-url');
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה ביצירת קישור Google');
      setGoogleConnecting(false);
    }
  }

  async function disconnectGoogle() {
    if (!window.confirm('לנתק את חשבון Google? הסנכרון יפסיק לעבוד.')) return;
    try {
      await api.delete('/settings/google/disconnect');
      setGoogleStatus({ connected: false });
    } catch { alert('שגיאה בניתוק'); }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
    </div>
  );

  const tabs = [
    { key: 'general', label: 'כללי' },
    { key: 'statuses', label: 'סטטוסים' },
    { key: 'service-calls', label: 'קריאות שירות' },
    { key: 'favorite-items', label: 'פריטים מועדפים' },
    { key: 'calls', label: 'שיחות' },
    { key: 'integrations', label: 'אינטגרציות' },
    ...(isAdmin ? [{ key: 'tokens', label: 'API Tokens' }, { key: 'users', label: 'משתמשים' }] : []),
  ];

  return (
    <div className="p-6 space-y-4 max-w-4xl" dir="rtl">
      <div className="flex items-center gap-3">
        <SettingsIcon className="text-primary-600 dark:text-primary-400" size={22} />
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">הגדרות</h1>
        {saved && <span className="text-sm text-green-600 dark:text-green-400 font-medium">✓ נשמר</span>}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 gap-1">
        {tabs.map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
              ${tab === key
                ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}>
            {label}
          </button>
        ))}
      </div>

      {/* General */}
      {tab === 'general' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
          <h2 className="font-semibold text-gray-900 dark:text-white">הגדרות כלליות</h2>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">שם העסק</label>
            <input
              defaultValue={settings.business_name}
              onBlur={(e) => saveSetting('business_name', e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              הדגשת כפתור רענון שיחות — לאחר כמה שניות ללא רענון
            </label>
            <input
              key={settings.stale_refresh_seconds}
              type="number"
              min="30"
              max="7200"
              defaultValue={settings.stale_refresh_seconds ?? 300}
              onBlur={(e) => {
                const v = parseInt(e.target.value);
                if (v >= 30) saveSetting('stale_refresh_seconds', v);
              }}
              className="w-32 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ברירת מחדל: 300 שניות (5 דקות)</p>
          </div>
        </div>
      )}

      {/* Statuses */}
      {tab === 'statuses' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">סטטוסי לקוחות</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            סטטוסים מערכתיים (מתעניין, לקוח, סוכן) לא ניתנים למחיקה ומתעדכנים אוטומטית.
            כשלקוח מוגדר בסטטוס ידני, הסטטוס שלו לא ישתנה אוטומטית.
          </p>

          <div className="space-y-2">
            {statuses.map((s) => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                {editingStatusId === s.id ? (
                  <>
                    <input
                      autoFocus
                      value={editStatusForm.name}
                      onChange={(e) => setEditStatusForm((f) => ({ ...f, name: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && saveEditStatus()}
                      className="flex-1 px-2 py-1 text-sm rounded-lg border border-primary-300 dark:border-primary-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <input type="color" value={editStatusForm.color}
                      onChange={(e) => setEditStatusForm((f) => ({ ...f, color: e.target.value }))}
                      className="h-8 w-10 rounded border border-gray-200 dark:border-gray-600 cursor-pointer" />
                    <button onClick={saveEditStatus} className="p-1 text-primary-600 hover:text-primary-700">
                      <Check size={15} />
                    </button>
                    <button onClick={() => setEditingStatusId(null)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <div className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">{s.name}</span>
                    {s.auto_rule && (
                      <span className="text-xs text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded-full">
                        אוטומטי
                      </span>
                    )}
                    {isAdmin && (
                      <button onClick={() => startEditStatus(s)}
                        className="p-1 text-gray-400 hover:text-primary-600 transition-colors">
                        <Pencil size={13} />
                      </button>
                    )}
                    {s.is_system ? (
                      <span className="text-xs text-gray-400">מערכתי</span>
                    ) : (
                      isAdmin && (
                        <button onClick={() => deleteStatus(s.id)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      )
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <input
                value={newStatusName}
                onChange={(e) => setNewStatusName(e.target.value)}
                placeholder="שם סטטוס חדש"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyDown={(e) => e.key === 'Enter' && addStatus()}
              />
              <input type="color" value={newStatusColor} onChange={(e) => setNewStatusColor(e.target.value)}
                className="h-10 w-14 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer" />
              <button onClick={addStatus}
                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
                <Plus size={15} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Service Call Types */}
      {tab === 'service-calls' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">סוגי קריאות שירות</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            סוגים מערכתיים (תיקון, הדרכה) לא ניתנים למחיקה.
          </p>

          <div className="space-y-2">
            {serviceCallTypes.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                <span className="text-sm font-medium text-gray-900 dark:text-white flex-1">{t.name}</span>
                {t.is_system ? (
                  <span className="text-xs text-gray-400">מערכתי</span>
                ) : isAdmin ? (
                  <button onClick={() => deleteServiceCallType(t.id)}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors">
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="flex gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              <input
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="שם סוג חדש (לדוגמה: התקנה)"
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyDown={(e) => e.key === 'Enter' && addServiceCallType()}
              />
              <button onClick={addServiceCallType}
                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
                <Plus size={15} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Favorite Items */}
      {tab === 'favorite-items' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-white">פריטים מועדפים להזמנות</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            פריטים שיופיעו כתגיות בחלון הזמנה חדשה — הקשה מוסיפה אותם אוטומטית עם המחיר.
          </p>

          <div className="space-y-2">
            {getFavItems().map((fav, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                {editingFavIndex === i ? (
                  <>
                    <input
                      autoFocus
                      value={editFavForm.name}
                      onChange={(e) => setEditFavForm((f) => ({ ...f, name: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && saveFavoriteItem(i)}
                      className="flex-1 px-2 py-1 text-sm rounded-lg border border-primary-300 dark:border-primary-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <input
                      type="number"
                      value={editFavForm.price}
                      onChange={(e) => setEditFavForm((f) => ({ ...f, price: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && saveFavoriteItem(i)}
                      placeholder="₪"
                      className="w-24 px-2 py-1 text-sm rounded-lg border border-primary-300 dark:border-primary-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                      dir="ltr"
                    />
                    <button onClick={() => saveFavoriteItem(i)} className="p-1 text-primary-600 hover:text-primary-700">
                      <Check size={15} />
                    </button>
                    <button onClick={() => setEditingFavIndex(null)} className="p-1 text-gray-400 hover:text-gray-600">
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200">{fav.name}</span>
                    {fav.price != null && (
                      <span className="text-sm text-gray-500 dark:text-gray-400" dir="ltr">₪{fav.price.toLocaleString()}</span>
                    )}
                    <button onClick={() => { setEditingFavIndex(i); setEditFavForm({ name: fav.name, price: fav.price ?? '' }); }}
                      className="p-1 text-gray-400 hover:text-primary-600">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => removeFavoriteItem(i)} className="p-1 text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            ))}
            {getFavItems().length === 0 && (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-2">אין פריטים מועדפים עדיין</p>
            )}
          </div>

          <div className="flex gap-2 items-center pt-2 border-t border-gray-100 dark:border-gray-700">
            <input
              value={newFavName}
              onChange={(e) => setNewFavName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addFavoriteItem()}
              placeholder="שם פריט"
              className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <input
              type="number"
              value={newFavPrice}
              onChange={(e) => setNewFavPrice(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addFavoriteItem()}
              placeholder="₪ מחיר"
              className="w-28 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
            />
            <button onClick={addFavoriteItem}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium">
              <Plus size={14} /> הוסף
            </button>
          </div>
        </div>
      )}

      {/* Calls Settings */}
      {tab === 'calls' && (
        <div className="space-y-6">

          {/* Display settings */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">הגדרות תצוגת שיחות</h2>

            {/* Group by phone toggle */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">קיבוץ שיחות לפי טלפון</p>
                <p className="text-xs text-gray-400 mt-0.5">שיחות מאותו מספר מתמזגות לשורה אחת עם אפשרות הרחבה</p>
              </div>
              <button onClick={() => saveSetting('calls_group_by_phone', !settings.calls_group_by_phone)}>
                {settings.calls_group_by_phone !== false
                  ? <ToggleRight size={28} className="text-primary-600" />
                  : <ToggleLeft size={28} />
                }
              </button>
            </div>

            {/* Days back for call count */}
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">ימים אחורה לספירת שיחות</p>
                <p className="text-xs text-gray-400 mt-0.5">0 = כל השיחות. N = רק שיחות מ-N ימים אחרונים</p>
              </div>
              <input
                type="number"
                min="0"
                value={settings.call_count_days_back ?? 0}
                onChange={(e) => setSettings((s) => ({ ...s, call_count_days_back: parseInt(e.target.value) || 0 }))}
                onBlur={(e) => saveSetting('call_count_days_back', parseInt(e.target.value) || 0)}
                className="w-20 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Call label types (מסע) */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">תגיות מסע</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">תגיות שמוצמדות לשיחה על ידי מערכת הטלפוניה (לפי ערך מספרי)</p>

            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">תווית</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">ערך (API)</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400 w-20">סדר</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {callLabels.map((lbl) => (
                  <tr key={lbl.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    {editingCallLabel?.id === lbl.id ? (
                      <>
                        <td className="py-2 pr-1">
                          <input value={editingCallLabel.label} onChange={(e) => setEditingCallLabel((x) => ({ ...x, label: e.target.value }))}
                            className="w-full px-2 py-1 text-xs rounded border border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none" />
                        </td>
                        <td className="py-2 pr-2">
                          <input value={editingCallLabel.value} onChange={(e) => setEditingCallLabel((x) => ({ ...x, value: e.target.value }))}
                            className="w-full px-2 py-1 text-xs rounded border border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none font-mono" />
                        </td>
                        <td className="py-2 pr-2">
                          <input type="number" value={editingCallLabel.sort_order ?? ''} onChange={(e) => setEditingCallLabel((x) => ({ ...x, sort_order: e.target.value }))}
                            className="w-16 px-2 py-1 text-xs rounded border border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none" />
                        </td>
                        <td className="py-2 flex gap-1 justify-end">
                          <button onClick={saveCallLabel} className="p-1 text-emerald-500 hover:text-emerald-700"><Check size={14} /></button>
                          <button onClick={() => setEditingCallLabel(null)} className="p-1 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2">{lbl.label}</td>
                        <td className="py-2 font-mono text-xs text-gray-500 dark:text-gray-400">{lbl.value}</td>
                        <td className="py-2 text-xs text-gray-400">{lbl.sort_order}</td>
                        <td className="py-2">
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => setEditingCallLabel({ ...lbl })} className="p-1 text-gray-400 hover:text-primary-600"><Pencil size={13} /></button>
                            <button onClick={() => deleteCallLabel(lbl.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={13} /></button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {isAdmin && (
              <div className="flex gap-2 items-end pt-2 border-t border-gray-100 dark:border-gray-700">
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">תווית</p>
                  <input value={newCallLabel.label} onChange={(e) => setNewCallLabel((x) => ({ ...x, label: e.target.value }))}
                    placeholder="למשל: עדויות"
                    className="w-full px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="w-24">
                  <p className="text-xs text-gray-500 mb-1">ערך (API)</p>
                  <input value={newCallLabel.value} onChange={(e) => setNewCallLabel((x) => ({ ...x, value: e.target.value }))}
                    placeholder="7012"
                    className="w-full px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono" />
                </div>
                <button onClick={addCallLabel}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm transition-colors">
                  <Plus size={14} /> הוסף
                </button>
              </div>
            )}
          </div>

          {/* Interest types */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">תחומי עניין</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">תגיות תחום עניין שניתן לצרף ללקוחות</p>

            <div className="space-y-1">
              {interestTypes.map((it) => (
                <div key={it.id} className="flex items-center gap-2 py-1.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                  {editingInterestType?.id === it.id ? (
                    <>
                      <input value={editingInterestType.name} onChange={(e) => setEditingInterestType((x) => ({ ...x, name: e.target.value }))}
                        className="flex-1 px-2 py-1 text-sm rounded border border-primary-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none" />
                      <button onClick={saveInterestType} className="p-1 text-emerald-500 hover:text-emerald-700"><Check size={14} /></button>
                      <button onClick={() => setEditingInterestType(null)} className="p-1 text-gray-400 hover:text-gray-600"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">{it.name}</span>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <button onClick={() => setEditingInterestType({ ...it })} className="p-1 text-gray-400 hover:text-primary-600"><Pencil size={13} /></button>
                          <button onClick={() => deleteInterestType(it.id)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 size={13} /></button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>

            {isAdmin && (
              <div className="flex gap-2 pt-2">
                <input value={newInterestName} onChange={(e) => setNewInterestName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addInterestType()}
                  placeholder="שם תחום עניין חדש..."
                  className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <button onClick={addInterestType}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm transition-colors">
                  <Plus size={14} /> הוסף
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Integrations */}
      {tab === 'integrations' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-5">
          <h2 className="font-semibold text-gray-900 dark:text-white">אינטגרציות</h2>

          {/* Google Contacts */}
          <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white text-sm">סנכרון אנשי קשר Google</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                עדכון שמות לקוחות בחשבון Google שלך בכל שינוי סטטוס — מסתנכרן אוטומטית לטלפון
              </p>
            </div>

            {/* Connection status */}
            {isAdmin && (
              <div>
                {googleStatus === null ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <Loader size={12} className="animate-spin" /> טוען...
                  </div>
                ) : googleStatus.connected ? (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-green-700 dark:text-green-400">חשבון מחובר</p>
                        <p className="text-xs text-green-600 dark:text-green-500 font-mono" dir="ltr">{googleStatus.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={disconnectGoogle}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Unlink size={12} /> נתק
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={connectGoogle}
                    disabled={googleConnecting}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors disabled:opacity-60"
                  >
                    {googleConnecting ? (
                      <Loader size={16} className="animate-spin" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    )}
                    {googleConnecting ? 'מעביר לGoogle...' : 'חבר חשבון Google'}
                  </button>
                )}
              </div>
            )}

            {/* Enable/disable sync toggle — only relevant when connected */}
            {googleStatus?.connected && (
              <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">הפעל סנכרון אוטומטי</p>
                  <p className="text-xs text-gray-400 mt-0.5">עדכון איש קשר בכל שינוי סטטוס לקוח</p>
                </div>
                <button
                  onClick={() => saveSetting('google_contacts_sync_enabled', !settings.google_contacts_sync_enabled)}
                  className="text-gray-400 hover:text-primary-600 transition-colors"
                >
                  {settings.google_contacts_sync_enabled
                    ? <ToggleRight size={28} className="text-primary-600" />
                    : <ToggleLeft size={28} />
                  }
                </button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* API Tokens */}
      {tab === 'tokens' && isAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">API Tokens</h2>
            <button onClick={createToken}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
              <Plus size={15} />
              צור טוקן
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            טוקני API משמשים לחיבור מערכת הטלפוניה שלך לרישום שיחות נכנסות.
            שלח את הטוקן ב-Header: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded font-mono">X-API-Key</code>
          </p>

          {newToken && (
            <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-2">
                ⚠️ שמור את הטוקן — לא יוצג שוב!
              </p>
              <code className="block text-xs font-mono bg-yellow-100 dark:bg-yellow-900/40 p-2 rounded break-all" dir="ltr">
                {newToken.token}
              </code>
              <button onClick={() => { navigator.clipboard.writeText(newToken.token); }}
                className="mt-2 flex items-center gap-1 text-xs text-yellow-700 dark:text-yellow-400 hover:underline">
                <Copy size={11} /> העתק
              </button>
            </div>
          )}

          <div className="space-y-2">
            {tokens.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">אין טוקנים</p>
            ) : (
              tokens.filter((t) => t.is_active !== false).map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t.name}</p>
                    <p className="text-xs text-gray-400">
                      נוצר {new Date(t.created_at).toLocaleDateString('he-IL')}
                      {t.last_used_at && ` · שימוש אחרון ${new Date(t.last_used_at).toLocaleDateString('he-IL')}`}
                    </p>
                  </div>
                  <button onClick={() => revokeToken(t.id)}
                    className="px-3 py-1.5 text-xs rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                    בטל
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Users */}
      {tab === 'users' && isAdmin && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 dark:text-white">משתמשים</h2>
            {!showAddUser && (
              <button onClick={() => setShowAddUser(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
                <Plus size={15} /> הוסף משתמש
              </button>
            )}
          </div>

          {/* Add user form */}
          {showAddUser && (
            <div className="p-4 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">משתמש חדש</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">שם מלא *</label>
                  <input
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="ישראל ישראלי"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">אימייל *</label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    dir="ltr"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">סיסמה *</label>
                  <input
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, password: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">תפקיד</label>
                  <select
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm((f) => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="user">משתמש רגיל</option>
                    <option value="admin">מנהל</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={addUser}
                  className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
                  צור משתמש
                </button>
                <button onClick={() => { setShowAddUser(false); setNewUserForm({ name: '', email: '', password: '', role: 'user' }); }}
                  className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition-colors">
                  ביטול
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.id}>
                {editingUser?.id === u.id ? (
                  /* Edit form */
                  <div className="p-4 rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">שם מלא</label>
                        <input value={editUserForm.name} onChange={(e) => setEditUserForm((f) => ({ ...f, name: e.target.value }))}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">תפקיד</label>
                        <select value={editUserForm.role} onChange={(e) => setEditUserForm((f) => ({ ...f, role: e.target.value }))}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                          <option value="user">משתמש רגיל</option>
                          <option value="admin">מנהל</option>
                        </select>
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">סיסמה חדשה (השאר ריק לשמירה)</label>
                        <input type="password" value={editUserForm.password} onChange={(e) => setEditUserForm((f) => ({ ...f, password: e.target.value }))}
                          placeholder="••••••••"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveEditUser}
                        className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors">
                        שמור
                      </button>
                      <button onClick={() => setEditingUser(null)}
                        className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm transition-colors">
                        ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal row */
                  <div className={`flex items-center gap-3 p-3 rounded-lg ${u.is_active ? 'bg-gray-50 dark:bg-gray-700' : 'bg-gray-50/50 dark:bg-gray-700/50 opacity-60'}`}>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {u.name}
                        {u.id === currentUser?.id && <span className="mr-1.5 text-xs text-primary-500 font-normal">(אתה)</span>}
                      </p>
                      <p className="text-xs text-gray-400"><span dir="ltr">{u.email}</span> · {u.role === 'admin' ? 'מנהל' : 'משתמש'}</p>
                    </div>
                    {!u.is_active && (
                      <span className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">מושהה</span>
                    )}
                    <button onClick={() => startEditUser(u)}
                      className="p-1.5 rounded text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                      title="ערוך משתמש">
                      <Pencil size={13} />
                    </button>
                    {u.id !== currentUser?.id && (
                      <button
                        onClick={() => toggleActive(u.id, u.is_active)}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                          u.is_active
                            ? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                            : 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                        }`}
                      >
                        {u.is_active ? 'השהה' : 'הפעל'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
